import React, { useState, useMemo } from "react";
import { Search, Wallet, Layers } from "lucide-react";

export default function CashBalance({ data = [], totalValueFromKPICard = [], onClose }) {
  const [searchTerm, setSearchTerm] = useState("");

  // Process and filter ledger data safely
  const ledgerData = useMemo(() => {
    return data || [];
  }, [data]);

  // Handle Search Filtering
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return ledgerData;
    return ledgerData.filter((row) =>
      (row.AccountName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (row.Account_Code || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [ledgerData, searchTerm]);

  // Calculate Total Balance dynamically, or fall back to KPI card total if present
  const totalBalance = useMemo(() => {
    if (searchTerm.trim()) {
      // If filtering, show the filtered total
      return filteredData.reduce((acc, row) => acc + (Number(row.CashBalance) || 0), 0);
    }
    // Otherwise try to use KPI card value, fallback to dynamic sum
    if (totalValueFromKPICard?.length > 0 && totalValueFromKPICard[0]?.["CashBalance"] !== undefined) {
      return Number(totalValueFromKPICard[0]["CashBalance"]) || 0;
    }
    return filteredData.reduce((acc, row) => acc + (Number(row.CashBalance) || 0), 0);
  }, [filteredData, searchTerm, totalValueFromKPICard]);

  return (
    /* Outer container with explicit height constraint matching BankBalance */
    <div className="flex flex-col h-[600px] max-h-[90vh] w-full rounded-2xl overflow-hidden border border-emerald-100/50 ">
      
      {/* MIDDLE SCROLLABLE AREA */}
      <div className="flex-1 overflow-y-auto min-h-0 p-4 md:p-6 space-y-4">
        
        {/* TOTAL CARDS SECTION - Vertical on Mobile, Horizontal on Desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Total Cash Balance Card (Soft Pastel Indigo/Lavender) */}
          <div className="bg-gradient-to-br from-indigo-50/70 to-indigo-100/40 border border-indigo-100 p-4 rounded-xl flex items-center justify-between shadow-sm">
            <div className="space-y-1">
              <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider block">
                Total Cash Balance
              </span>
              <span className="text-base font-extrabold text-indigo-950">
                {totalBalance.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="p-2.5 bg-indigo-500/10 rounded-xl">
              <Wallet className="h-5 w-5 text-indigo-600" />
            </div>
          </div>

          {/* Total Cash Accounts Card (Soft Pastel Sage/Teal) */}
          <div className="bg-gradient-to-br from-teal-50/70 to-teal-100/40 border border-teal-100 p-4 rounded-xl flex items-center justify-between shadow-sm">
            <div className="space-y-1">
              <span className="text-[10px] font-bold text-teal-500 uppercase tracking-wider block">
                Total Cash Accounts
              </span>
              <span className="text-base font-extrabold text-teal-950">
                {filteredData.length}
              </span>
            </div>
            <div className="p-2.5 bg-teal-500/10 rounded-xl">
              <Layers className="h-5 w-5 text-teal-600" />
            </div>
          </div>
        </div>

        {/* Search Input Bar */}
        <div className="relative max-w-xs">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search className="h-3.5 w-3.5 text-slate-400" />
          </span>
          <input
            type="text"
            className="block w-full pl-9 pr-3 py-1.5 text-xs text-slate-900 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all placeholder:text-slate-400"
            placeholder="Search cash account..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Accounts Table List */}
        <div className="border border-slate-150 bg-white rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-150">
              <thead>
                <tr className="bg-slate-50/50">
                  <th className="px-4 py-2.5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Account Name
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Cash Balance
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredData.length > 0 ? (
                  filteredData.map((row, idx) => (
                    <tr
                      key={row.Account_Code || idx}
                      className="hover:bg-slate-50/45 transition-colors"
                    >
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="text-xs font-semibold text-slate-800">
                            {row.AccountName}
                          </span>
                          <span className="text-[9px] text-slate-400 font-mono">
                            {row.Account_Code || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <span className="text-xs font-bold text-indigo-600">
                          {Number(row.CashBalance || 0).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2" className="text-center py-10 text-slate-400">
                      <p className="text-xs">No cash accounts match your search.</p>
                    </td>
                  </tr>
                )}
              </tbody>
              {/* Bottom Summary Row inline with table */}
              {filteredData.length > 0 && (
                <tfoot>
                  <tr className="bg-slate-50/50 border-t border-slate-200">
                    <td className="px-4 py-2.5 text-xs font-bold text-slate-600">
                      Total Ledger Cash
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs font-extrabold text-indigo-700">
                      {totalBalance.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {/* FIXED FOOTER */}
      
        <div className="flex-none p-4 md:px-6 md:py-4 border-t border-emerald-100/55 backdrop-blur-md">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl text-sm font-semibold hover:shadow-lg hover:opacity-95 active:scale-[0.99] transition-all tracking-wider uppercase"
          >
            Close Window
          </button>
        </div>
      

    </div>
  );
}